import { useTranslation } from "react-i18next";
import { Button, Divider } from "react-native-paper";
import type { IconType, LevelIconName } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { ItemTypeListItem } from "../ItemTypeListItem";
import { ProgressCardShell } from "./ProgressCardShell";

export type ItemTypeProgressRow = {
	id: string;
	icon: IconType;
	title: string;
	subtitle?: string;
	tooltip?: string;
	level?: string;
	levelIcon?: LevelIconName;
};

type Props = {
	title: string;
	subtitle?: string;
	icon?: IconType;
	rows: ItemTypeProgressRow[];
	tip?: string;
	fillHeight?: boolean;
	tipLoading?: boolean;
	onViewAllPress?: () => void;
	onViewDetails?: (rowId: string) => void;
};

export const ItemTypesCard = ({
	title,
	subtitle,
	icon = "book-open-outline",
	rows,
	tip,
	fillHeight,
	tipLoading,
	onViewAllPress,
	onViewDetails,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const shouldShowViewAll = Boolean(onViewAllPress);

	return (
		<ProgressCardShell
			icon={icon}
			title={title}
			subtitle={subtitle}
			tip={tip}
			fillHeight={fillHeight}
			tipLoading={tipLoading}
		>
			{rows.map((row, index) => (
				<Box key={row.id}>
					{index > 0 ? (
						<Divider
							style={{
								marginHorizontal: 16,
								backgroundColor: theme.colors.surfaceVariant,
							}}
						/>
					) : null}
					<ItemTypeListItem
						icon={row.icon}
						title={row.title}
						subtitle={row.subtitle}
						tooltip={row.tooltip}
						level={
							row.level ? { label: row.level, icon: row.levelIcon } : undefined
						}
						onPress={onViewDetails ? () => onViewDetails(row.id) : undefined}
					/>
				</Box>
			))}
			{shouldShowViewAll ? (
				<Box alignItems="flex-end" paddingHorizontal={16} paddingBottom={8}>
					<Button compact mode="text" onPress={onViewAllPress}>
						{t("common.viewAll")}
					</Button>
				</Box>
			) : null}
		</ProgressCardShell>
	);
};
